// The signed-in session (server plan §4.4): the person, the account and location they act in, their permissions
// there, and a ten-minute access token held in memory only. The refresh token lives in an httpOnly cookie the page
// never sees; a reload, a token about to expire or a location switch asks the server for a new access token with it.
// The server verifies every call; what the page reads here only decides what it offers.
import type { AuthClient } from '@radish/api-client';
import { REFRESH_SCOPE_LOST, type LocationChoice, type SessionView, type SignedIn } from '@radish/contracts';
import { localExpiry } from './token-clock.ts';

export interface Session {
  readonly token: string;
  readonly expiresAt: number;
  readonly view: SessionView & { readonly current: NonNullable<SessionView['current']> };
}

let signed: SignedIn | null = null;
/** When the access token expires on this tab's clock (B1119): its lifetime as the server minted it, from its arrival. */
let expiresHere: number | null = null;
let auth: AuthClient | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;
let inFlight: Promise<boolean> | null = null;
/** Counts sign-outs: a refresh answered after one began belongs to the ended session and is dropped. */
let signOuts = 0;
/** The server's sign-out while it runs: a refresh waits for it, so the cookie it is revoking can't sign the tab back in. */
let ending: Promise<unknown> | null = null;
/**
 * The person signed out: no refresh runs until the next sign-in. The server's sign-out can fail (a network error or a
 * 5xx) and leave the refresh cookie alive, and the login page's guard would otherwise sign them back in with it. Kept in
 * localStorage too, so a reload or another tab stays signed out, as legacy's does once it has deleted its stored token;
 * the in-memory copy holds when storage is unavailable.
 */
let signedOut = false;
const SIGNED_OUT_KEY = 'radish.backoffice.signedOut';

function markSignedOut(on: boolean): void {
  signedOut = on;
  try {
    if (on) window.localStorage.setItem(SIGNED_OUT_KEY, 'true');
    else window.localStorage.removeItem(SIGNED_OUT_KEY);
  } catch {
    // Storage refused (private mode, blocked site data): this tab's memory still holds it.
  }
}

/**
 * Another tab signed out: this one ends at once, as legacy's tabs do when its stored token is deleted, rather than at
 * its next refresh.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === SIGNED_OUT_KEY && event.newValue === 'true' && signed !== null) signOut();
  });
}

function isSignedOut(): boolean {
  if (signedOut) return true;
  try {
    return window.localStorage.getItem(SIGNED_OUT_KEY) === 'true';
  } catch {
    return false;
  }
}
const listeners = new Set<() => void>();

/** The account and location a tab lost at its last refresh, and the location's name, until the layout has said so. */
export interface LostScope { readonly accountId: string; readonly locationId: string | null; readonly name: string }
let lostScope: LostScope | null = null;

const notify = () => listeners.forEach((l) => l());
const MAX_TIMER_MS = 2_147_483_647;

/** The client the session refreshes with (set once at start-up). */
export function bindAuthClient(client: AuthClient): void {
  auth = client;
}

/**
 * Refresh a minute before the access token expires, so calls never carry an expired one. The expiry is counted on this
 * tab's clock from the token's own lifetime, never the server's `expiresAt` against `Date.now()`: a computer clock nine
 * minutes fast renewed every five seconds, and one running slow renewed after the token had expired (B1119).
 */
function schedule(): void {
  if (timer !== undefined) clearTimeout(timer);
  const at = expiresHere;
  if (at === null || auth === null) return;
  // setTimeout fires at once for a delay past 2^31 - 1 ms, so a far expiry is clamped rather than refreshed in a loop.
  timer = setTimeout(() => void refreshSession(), Math.min(MAX_TIMER_MS, Math.max(5_000, at - Date.now() - 60_000)));
}

/** The session with an access token, or null when signed out or still choosing an account. */
export function currentSession(): Session | null {
  if (signed === null || signed.accessToken === null || signed.expiresAt === null || signed.session.current === null) return null;
  return { token: signed.accessToken, expiresAt: signed.expiresAt, view: signed.session as Session['view'] };
}

/**
 * Signed in, but an account must be chosen first (Radish staff, resellers). A Radish admin or reseller then holds an
 * account-less token that only creates accounts (and reads them for the Edit Account dialog).
 */
export function choosingAccount(): SessionView | null {
  return signed !== null && signed.session.current === null ? signed.session : null;
}

/** The token every call carries: the session's, or the account-less one while choosing an account (or none). */
export function bearerToken(): string | null {
  return signed?.accessToken ?? null;
}

export function signIn(next: SignedIn): void {
  expired = false;
  markSignedOut(false);
  signed = next;
  expiresHere = next.accessToken === null || next.expiresAt === null ? null : localExpiry(next.accessToken, next.expiresAt, Date.now());
  schedule();
  notify();
}

/** Whether the last session ended because the server stopped accepting it (not the person signing out). */
let expired = false;

/**
 * Ends the session in this tab. `expired`: the server stopped accepting it and the refresh failed, so the login page
 * says so (legacy's "Your session has expired. Please log in again.", once: REG-006).
 */
export function signOut(reason: 'signed_out' | 'expired' = 'signed_out'): void {
  if (reason === 'expired' && signed !== null) expired = true;
  signOuts += 1;
  signed = null;
  expiresHere = null;
  lostScope = null;
  if (timer !== undefined) clearTimeout(timer);
  timer = undefined;
  notify();
}

/**
 * The login page's search for a redirect: a fresh `time` re-runs its guard (a number, so the URL reads legacy's bare
 * `?time=<ms>`, not the router's JSON-quoted `%22<ms>%22` for a string; Q-NAV-16), and `expired` makes it say the session
 * expired, from the moment the last one expired until the next sign-in (REG-006).
 */
export function loginSearch(): { readonly time: number; readonly expired?: true } {
  return { time: Date.now(), ...(expired ? { expired: true as const } : {}) };
}

/** Ends the session on the server too (the refresh family is revoked and the cookie cleared). */
export async function endSession(): Promise<void> {
  // The login page's guard refreshes at once to see whether anyone is signed in; until the server has revoked the
  // refresh cookie that refresh would succeed and sign the person straight back in (seen in the passkey journey). So the
  // tab is marked signed out and the server's sign-out is pending before signOut() tells the listeners (the router).
  markSignedOut(true);
  const done = auth?.signOut() ?? Promise.resolve();
  ending = done;
  signOut();
  try {
    await done;
  } finally {
    if (ending === done) ending = null;
  }
}

const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));


/** The scope this tab lost (A192: the layout sends the tab to the location picker with a toast), or null. */
export const lostScopeSnapshot = (): LostScope | null => lostScope;

/** The layout has told the person and sent the tab to the picker. */
export function clearLostScope(): void {
  lostScope = null;
}

/**
 * One refresh at this tab's own scope (ops plan 2026-10-23-tab-scoped-refresh, WF-006, REG-003). Every tab of a browser
 * shares the refresh cookie, whose family holds the scope the last switch in any tab chose, so a tab acting somewhere
 * names its account and location and the server mints the token there, changing nothing stored. When this person may no
 * longer act there (403 `scope_lost`, the cookie kept), the tab never moves silently: the scope is noted for the layout,
 * which sends the tab to the location picker with a toast (A192), and the plain refresh gives the session wherever the
 * person may still act, or ends it. `lost`: the call that needed the token must not be retried there.
 */
async function refreshHere(client: AuthClient): Promise<{ readonly result: Awaited<ReturnType<AuthClient['refresh']>>; readonly lost: boolean }> {
  const here = signed?.session.current ?? null;
  if (here === null) return { result: await client.refresh(), lost: false };
  const result = await client.refresh({ accountId: here.accountId, locationId: here.locationId });
  if (result.ok || result.status !== 403 || result.error.code !== REFRESH_SCOPE_LOST) return { result, lost: false };
  const account = signed?.session.accounts.find((a) => a.accountId === here.accountId);
  const name = account?.locations.find((l) => l.locationId === here.locationId)?.name ?? account?.name ?? 'this location';
  lostScope = { accountId: here.accountId, locationId: here.locationId, name };
  return { result: await client.refresh(), lost: true };
}

/**
 * A new access token from the refresh cookie. One at a time per tab; when another tab refreshed the same cookie a
 * moment ago the server answers "in flight", and the next try uses the cookie that tab received. Only the server
 * rejecting the cookie (401 or 403) ends the session, as expired; a network error or a server error keeps it, so a blip
 * never forces a new sign-in (review of #1467).
 */
export function refreshSession(): Promise<boolean> {
  // A session that just expired has a dead cookie: the route guards don't ask again until the person signs in (REG-006).
  if (expired) return Promise.resolve(false);
  // Signed out (in this tab, before a reload, or in another tab): only a new sign-in starts a session, whatever the
  // server's sign-out answered. A tab still holding a session ends it here, as legacy's tabs share its deleted token.
  if (isSignedOut()) {
    if (signed !== null) signOut();
    return Promise.resolve(false);
  }
  inFlight ??= (async () => {
    try {
      if (ending !== null) await ending.catch(() => undefined);
      const started = signOuts;
      for (let attempt = 0; attempt < 4 && auth !== null; attempt += 1) {
        const { result, lost } = await refreshHere(auth);
        // Signed out while it was on its way, in this tab or another (a tab with no session yet hears of that only
        // through the mark): the answer is the ended session's, and signing in with it would lift the mark for every tab.
        if (signOuts !== started || isSignedOut()) {
          if (signed !== null) signOut();
          return false;
        }
        if (result.ok) {
          signIn(result.data);
          return !lost;
        }
        if (result.error.code !== 'refresh_in_flight') {
          if ((result.status === 401 || result.status === 403) && signed !== null) signOut('expired');
          return false;
        }
        await pause(400 * (attempt + 1));
      }
      return false;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/** The location the switcher was last used to pick, until the layout's switch there (B1727). */
let switcherPick: string | null = null;

/**
 * The location switcher's pick (legacy AppSidebarLocation → setDefaultLocation): the next switch to that location asks
 * the server to remember it, so a merchant's next sign-in starts there (EP-AUTH-05). A switch a typed URL makes, or any
 * other, leaves the default location where it was (B1727).
 */
export function markSwitcherPick(locationId: string): void {
  switcherPick = locationId;
}

/** Acts in another account or location (a new access token whose permissions are those held there). */
export async function switchTo(accountId: string, locationId: string | null): Promise<{ readonly ok: true } | { readonly ok: false; readonly message: string }> {
  if (auth === null) return { ok: false, message: 'Not signed in.' };
  // A switch runs on the refresh cookie alone, so after a sign-out whose server half failed it would sign the person
  // back in (and lift the mark for every tab): it ends this tab's session instead, as a refresh does.
  if (isSignedOut()) {
    if (signed !== null) signOut();
    return { ok: false, message: 'Not signed in.' };
  }
  const started = signOuts;
  // Used once: only the switch the pick leads to remembers it.
  const remember = locationId !== null && switcherPick === locationId;
  switcherPick = null;
  const result = await auth.switchTo(remember ? { accountId, locationId, remember } : { accountId, locationId });
  // Signed out while it was on its way (this tab, or another through the storage event): the answer is the ended
  // session's, and signing in with it would lift the mark for every tab (review of #1913).
  if (signOuts !== started || isSignedOut()) {
    if (signed !== null) signOut();
    return { ok: false, message: 'Not signed in.' };
  }
  if (!result.ok) {
    if (result.status === 401) signOut();
    return { ok: false, message: result.error.message };
  }
  signIn(result.data);
  return { ok: true };
}

/** Test seam: forgets every sign-in, sign-out and refresh this module holds, as a fresh page load does. */
export function resetSessionForTests(): void {
  if (timer !== undefined) clearTimeout(timer);
  timer = undefined;
  signed = null;
  expiresHere = null;
  auth = null;
  inFlight = null;
  ending = null;
  signOuts = 0;
  lostScope = null;
  switcherPick = null;
  markSignedOut(false);
  expired = false;
}

/** The current sign-in as one stable value (changes only when the session does), for useSyncExternalStore. */
export const sessionSnapshot = (): SignedIn | null => signed;

export function onSessionChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The locations of the account the session acts in (the location switcher). */
export function locationsOf(view: SessionView): readonly LocationChoice[] {
  return view.accounts.find((a) => a.accountId === view.current?.accountId)?.locations ?? [];
}

/** The two letters of the toolbar avatar (legacy shows the user's initials, "BT"). */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join('') || '?';
}
